import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { 
  Users,
  UserPlus,
  Mail,
  Trash2,
  Loader2,
  Crown,
  User,
  Clock,
  Send,
  X
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeamTab = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [sending, setSending] = useState(false);
  
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [manualLink, setManualLink] = useState(null);

  // Check if current user is org owner (no organization_id means they are owner)
  const isOwner = !user?.organization_id;

  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/team/members`, { withCredentials: true }),
        isOwner ? axios.get(`${BACKEND_URL}/api/team/invitations`, { withCredentials: true }) : Promise.resolve({ data: { invitations: [] } })
      ]);
      
      setMembers(membersRes.data.members || []);
      setInvitations(invitesRes.data.invitations || []);
    } catch (error) {
      console.error("Error fetching team data:", error);
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/team/invite`, 
        { email: inviteEmail, name: inviteName },
        { withCredentials: true }
      );
      
      if (response.data.email_sent) {
        toast.success("Invitation sent successfully!");
      } else if (response.data.invite_link) {
        setManualLink(response.data.invite_link);
      } else {
        toast.warning("Invitation created but email failed to send.");
      }
      
      setInviteEmail("");
      setInviteName("");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removeConfirm) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/team/members/${removeConfirm.user_id}`, {
        withCredentials: true
      });
      toast.success("Team member removed");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
    setRemoveConfirm(null);
  };

  const handleRevokeInvite = async () => {
    if (!revokeConfirm) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/team/invite/${revokeConfirm.id}`, {
        withCredentials: true
      });
      toast.success("Invitation revoked");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke invitation");
    }
    setRevokeConfirm(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-state">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-tab">
      {/* Team Members Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members
          </CardTitle>
          <CardDescription>
            {members.length} {members.length === 1 ? 'member' : 'members'} in your team
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.map((member) => (
              <div 
                key={member.user_id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {member.picture ? (
                    <img 
                      src={member.picture} 
                      alt={member.name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{member.name}</span>
                      {member.role === "owner" && (
                        <Crown className="w-4 h-4 text-amber-500" title="Organization Owner" />
                      )}
                      {member.is_current_user && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">You</span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">{member.email}</span>
                  </div>
                </div>
                {isOwner && member.role !== "owner" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRemoveConfirm(member)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`remove-member-${member.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite New Member Card - Only for owners */}
      {isOwner && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Invite Team Member
            </CardTitle>
            <CardDescription>
              Send an invitation to add a colleague to your team
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Name (optional)</Label>
                  <Input
                    id="invite-name"
                    type="text"
                    placeholder="John Doe"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    data-testid="invite-name-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={sending || !inviteEmail} data-testid="send-invite-button">
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Invitation
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Manual Invite Link Dialog */}
      {manualLink && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm font-medium mb-2">Email could not be sent. Share this link manually:</p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={manualLink}
                className="font-mono text-xs bg-white"
                data-testid="manual-invite-link"
              />
              <Button
                size="sm"
                variant="outline"
                data-testid="copy-invite-link"
                onClick={() => {
                  navigator.clipboard.writeText(manualLink);
                  toast.success("Link copied!");
                }}
              >
                Copy
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setManualLink(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Invitations Card - Only for owners */}
      {isOwner && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending Invitations
            </CardTitle>
            <CardDescription>
              {invitations.length} pending {invitations.length === 1 ? 'invitation' : 'invitations'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((invite) => (
                <div 
                  key={invite.id}
                  className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <span className="font-medium">{invite.name || invite.email}</span>
                      {invite.name && (
                        <p className="text-sm text-muted-foreground">{invite.email}</p>
                      )}
                      <p className="text-xs text-amber-600">
                        Expires {new Date(invite.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRevokeConfirm(invite)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`revoke-invite-${invite.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Not Owner Notice */}
      {!isOwner && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-800">
              Only the organization owner can invite new team members or manage the team.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Remove Member Dialog */}
      <AlertDialog open={!!removeConfirm} onOpenChange={() => setRemoveConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{removeConfirm?.name}</strong> ({removeConfirm?.email}) from your team? 
              They will lose access to all shared data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invitation Dialog */}
      <AlertDialog open={!!revokeConfirm} onOpenChange={() => setRevokeConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke the invitation sent to <strong>{revokeConfirm?.email}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRevokeInvite}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamTab;
